// MINESWEEPER: HARD MODE — the 5x5 board, and the mines are real.
//
// Pick a mine count, click the squares you are opening, and one beacon round reveals the whole
// board. The reveal draws the layout the contract derived, read back from its own pure view.
//
// It looks like the real thing because it borrows the real thing: the XP Minesweeper bitmaps
// (flags, mines, the LED counters, the face) already shipped with this desktop.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CHIP, minesQuote, revealMines } from 'lib/casino';
import sfx from 'lib/sfx';
import flagPng from 'assets/minesweeper/flag.png';
import minePng from 'assets/minesweeper/mine-ceil.png';
import mineDeathPng from 'assets/minesweeper/mine-death.png';
import smilePng from 'assets/minesweeper/smile.png';
import ohhPng from 'assets/minesweeper/ohh.png';
import winPng from 'assets/minesweeper/win.png';
import deadPng from 'assets/minesweeper/dead.png';
import { useBet, useStakeCap } from './state';
import { FONT, GroupBox, HouseRules, Led, Outcome, Panel, PlayButton, StakeBox, T, fmtChip, parseUnits, xpButton } from './ui';

const TILES = 25;
const MINE_OPTIONS = [1, 3, 5, 10];

const popcount = mask => {
  let n = 0;
  for (let i = 0; i < TILES; i += 1) if ((mask >> i) & 1) n += 1;
  return n;
};

export default function Mines({ casino }) {
  const [amount, setAmount] = useState('5');
  const [mines, setMines] = useState(5);
  const [pick, setPick] = useState(0b11111);
  const [quote, setQuote] = useState(null);
  const [layout, setLayout] = useState(null);
  const bet = useBet('mines', casino);

  const k = popcount(pick);
  const locked = bet.phase !== 'idle' && bet.phase !== 'done';

  // The payout and the chance both come from the contract, so the number on screen is the
  // number the machine will actually honour.
  useEffect(() => {
    let alive = true;
    setQuote(null);
    if (!casino.open || k === 0 || k > TILES - mines) return undefined;
    minesQuote(mines, k)
      .then(q => {
        if (alive) setQuote(q);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [mines, k, casino.open]);

  useEffect(() => {
    let alive = true;
    setLayout(null);
    if (!bet.result) return undefined;
    revealMines(bet.result.id, mines, bet.result.randomness)
      .then(m => {
        if (alive) setLayout(Number(m));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bet.result]);

  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BigInt(10000)) / BigInt(10500);
  const payout = quote ? (stake * quote.mult) / BigInt(10000) : BigInt(0);

  // A flag that survived is a square that opened: one solid thunk when the board reveals and
  // at least one pick came up clean. The win and the loss are the hook's to announce.
  useEffect(() => {
    if (layout === null) return;
    if (popcount(pick & ~layout) > 0) sfx.reelStop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  const tooMany = k > TILES - mines;
  const tooRich = quote && quote.mult > BigInt(1000000);
  const tooCheap = quote && quote.mult <= BigInt(10000);
  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('mines', quote ? Number(quote.mult) : 0, casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && k > 0 && !tooMany && !tooRich && !tooCheap && !overCap;

  const toggle = i => {
    if (locked) return;
    setPick(p => p ^ (1 << i));
  };

  const face =
    bet.phase === 'done' && bet.result ? (bet.result.won ? winPng : deadPng) : locked ? ohhPng : smilePng;

  return (
    <Div>
      <div className="mn__frame">
        <div className="mn__head">
          <Led text={String(mines).padStart(3, '0')} />
          <button type="button" className="mn__face" onClick={bet.phase === 'done' ? bet.reset : undefined} title={bet.phase === 'done' ? 'again' : 'the face'}>
            <img src={face} alt="" />
          </button>
          <Led text={String(k).padStart(3, '0')} />
        </div>

        <div className="mn__board">
          {Array.from({ length: TILES }).map((_, i) => {
            const picked = ((pick >> i) & 1) === 1;
            const isMine = layout !== null && ((layout >> i) & 1) === 1;
            const cls = [
              'mn__tile',
              picked ? 'picked' : '',
              layout !== null && isMine ? 'mine' : '',
              layout !== null && picked && !isMine ? 'safe' : '',
              layout !== null ? 'revealed' : '',
            ]
              .filter(Boolean)
              .join(' ');
            let img = null;
            if (layout !== null && isMine) img = picked ? mineDeathPng : minePng;
            else if (picked && layout === null) img = flagPng;
            return (
              <button key={i} type="button" className={cls} onClick={() => toggle(i)} disabled={locked}>
                {img ? <img src={img} alt="" /> : null}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mn__caption">
        {layout === null
          ? 'flag the squares you are opening · every flag must survive'
          : bet.result && bet.result.won
          ? 'every flag survived'
          : 'a flag was on a mine'}
      </div>

      <Panel>
        <GroupBox legend="Mines buried">
          <div className="mn__bar">
            {MINE_OPTIONS.map(m => (
              <button
                key={m}
                type="button"
                className={m === mines ? 'on' : ''}
                disabled={locked}
                onClick={() => setMines(m)}
              >
                {m}
              </button>
            ))}
            <span className="mn__count">{k} open</span>
          </div>
        </GroupBox>

        <div className="mn__quote">
          <div>
            <span className="k">Pays</span>
            <span className="v">{quote ? `${(Number(quote.mult) / 10000).toFixed(2)}x` : '—'}</span>
          </div>
          <div>
            <span className="k">Chance</span>
            <span className="v">
              {quote ? `${((Number(quote.chance) / 1e18) * 100).toFixed(2)}%` : '—'}
            </span>
          </div>
          <div>
            <span className="k">Returns</span>
            <span className="v">
              {fmtChip(payout)} {CHIP.symbol}
            </span>
          </div>
        </div>

        {tooMany ? (
          <div className="mn__warn">Only {TILES - mines} squares are safe with {mines} mines buried.</div>
        ) : null}
        {tooRich ? (
          <div className="mn__warn">That pick pays more than this cabinet will write. Open fewer squares.</div>
        ) : null}
        {tooCheap ? (
          <div className="mn__warn">That pick pays 1.00x or less, and the cabinet refuses it. Open more squares or bury more mines.</div>
        ) : null}

        <StakeBox value={amount} onChange={setAmount} disabled={locked} />

        <PlayButton
          label="SWEEP"
          onClick={() => bet.play(allIn, [mines, pick])}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        {bet.phase === 'stuck' ? (
          <div className="mn__warn">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset} />

        <HouseRules
          fair={false}
          practice={casino.practice}
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .mn__frame {
    background: #c0c0c0;
    border-left: 3px solid #fff;
    border-top: 3px solid #fff;
    border-right: 3px solid #808080;
    border-bottom: 3px solid #808080;
    padding: 6px;
    width: max-content;
    max-width: 100%;
    margin: 0 auto;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  }
  .mn__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-left: 2px solid #808080;
    border-top: 2px solid #808080;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    padding: 4px 5px;
    margin-bottom: 6px;
    gap: 8px;
  }
  .mn__face {
    width: 26px;
    height: 26px;
    padding: 0;
    background: #c0c0c0;
    border-left: 2px solid #fff;
    border-top: 2px solid #fff;
    border-right: 2px solid #808080;
    border-bottom: 2px solid #808080;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .mn__face:active {
    border-left-color: #808080;
    border-top-color: #808080;
    border-right-color: #fff;
    border-bottom-color: #fff;
  }
  .mn__face img {
    width: 17px;
    height: 17px;
    image-rendering: pixelated;
  }
  .mn__board {
    display: grid;
    grid-template-columns: repeat(5, 30px);
    gap: 0;
    border-left: 3px solid #808080;
    border-top: 3px solid #808080;
    border-right: 3px solid #fff;
    border-bottom: 3px solid #fff;
  }
  .mn__tile {
    width: 30px;
    height: 30px;
    padding: 0;
    box-sizing: border-box;
    background: #c0c0c0;
    border-left: 3px solid #f5f5f5;
    border-top: 3px solid #f5f5f5;
    border-right: 3px solid #7b7b7b;
    border-bottom: 3px solid #7b7b7b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .mn__tile img {
    width: 16px;
    height: 16px;
    image-rendering: pixelated;
  }
  .mn__tile.revealed,
  .mn__tile.safe {
    border: 1px solid #808080;
    background: #c0c0c0;
  }
  .mn__tile.revealed:not(.mine):not(.safe) {
    border: 3px solid transparent;
    border-left-color: #f5f5f5;
    border-top-color: #f5f5f5;
    border-right-color: #7b7b7b;
    border-bottom-color: #7b7b7b;
  }
  .mn__tile.safe {
    background: #d8f0d8;
  }
  .mn__tile.mine.picked {
    background: #ff0000;
  }
  .mn__tile:disabled {
    cursor: default;
  }
  .mn__caption {
    text-align: center;
    font: 10px ${FONT.web};
    color: ${T.cream};
    margin-top: 7px;
  }
  .mn__bar {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
  }
  .mn__bar button {
    ${xpButton};
    min-width: 34px;
    padding: 0 10px;
  }
  .mn__bar button.on {
    background: linear-gradient(180deg, #cdcac3, #e3e3db 8%, #e5e5de 94%, #f2f2f1);
    box-shadow: inset 1px 1px 2px rgba(0, 0, 0, 0.25);
    font-weight: 700;
  }
  .mn__count {
    margin-left: auto;
    font: 11px ${FONT.ui};
    color: #000;
  }
  .mn__quote {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .mn__quote > div {
    flex: 1;
    min-width: 92px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .mn__quote .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .mn__quote .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .mn__warn {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .mn__warn button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
`;
